import { parseDateLocal } from './utils';

/**
 * Enrollment Grouping Utility
 * 
 * This module provides standardized logic for grouping enrollment records by plan.
 * 
 * ARCHITECTURE NOTE:
 * - Employee enrollments have dependentId = null
 * - Dependent enrollments have dependentId = number
 * - One enrollment row per person covered (employee + each dependent)
 * - Always use this utility instead of manual grouping to avoid bugs
 */

export interface GroupedEnrollment {
  base: any;
  dependentIds: number[];
  waivedRows: any[];
}

/**
 * Groups enrollment records by planId, separating base (employee) enrollments
 * from dependent and waived enrollments.
 * 
 * Logic:
 * 1. Dependent rows (dependentId != null) are collected in dependentIds array
 * 2. Waived rows are collected in waivedRows array
 * 3. Only employee enrollments (dependentId = null, not waived) compete for "base"
 * 4. Base selection prefers: active status > pending/terminated, newer effectiveDate
 * 
 * @param enrollments - Array of enrollment records from the API
 * @returns Map of planId to GroupedEnrollment
 */
export function groupEnrollmentsByPlan(
  enrollments: any[]
): Map<number, GroupedEnrollment> {
  const byPlan = new Map<number, GroupedEnrollment>();
  
  for (const enrollment of enrollments) {
    const planId = enrollment.planId;
    if (!planId) continue;
    
    const group = byPlan.get(planId) || {
      base: null,
      dependentIds: [],
      waivedRows: [],
    };
    
    // Handle dependent enrollments - skip from base selection
    if (enrollment.dependentId) {
      if (!group.dependentIds.includes(enrollment.dependentId)) {
        group.dependentIds.push(enrollment.dependentId);
      }
      byPlan.set(planId, group);
      continue;
    }
    
    // Handle waived enrollments - collect separately, skip from base selection
    const isWaived = 
      enrollment.waivedCoverage || 
      enrollment.status?.toLowerCase() === 'waived';
    
    if (isWaived) {
      group.waivedRows.push(enrollment);
      byPlan.set(planId, group);
      continue;
    }
    
    // Select best base enrollment (only employee, non-waived enrollments reach here)
    if (!group.base) {
      group.base = enrollment;
    } else {
      const currentStatus = group.base.status?.toLowerCase();
      const newStatus = enrollment.status?.toLowerCase();
      const currentDate = group.base.effectiveDate
        ? parseDateLocal(group.base.effectiveDate) ?? new Date(0)
        : new Date(0);
      const newDate = enrollment.effectiveDate
        ? parseDateLocal(enrollment.effectiveDate) ?? new Date(0)
        : new Date(0);
      
      // Prefer active over pending/terminated
      if (newStatus === 'active' && currentStatus !== 'active') {
        group.base = enrollment;
      } 
      // If same status, prefer newer effective date
      else if (newStatus === currentStatus && newDate > currentDate) {
        group.base = enrollment;
      }
    }
    
    byPlan.set(planId, group);
  }
  
  return byPlan;
}
